import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { MedicalHistoryObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type MedicalHistoryStoreAction =
  | { type: "SET_MEDICAL_HISTORY"; payload: MedicalHistoryObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_MEDICAL_HISTORY"; payload: MedicalHistoryObj }
  | { type: "DELETE_MEDICAL_HISTORY"; payload: string }
  | { type: "SET_MEDICAL_HISTORY_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_MEDICAL_HISTORY_SAVING_NEW"; payload: boolean };

// Reducer Function
const medicalHistoryStoreReducer = (
  state: MedicalHistoryStoreState,
  action: MedicalHistoryStoreAction
): MedicalHistoryStoreState => {
  switch (action.type) {
    case "SET_MEDICAL_HISTORY":
      return {
        ...state,
        medicalHistory: action.payload,
        medicalHistoryLoading: false,
      };

    case "SET_LOADING":
      return { ...state, medicalHistoryLoading: action.payload };

    case "ADD_OR_UPDATE_MEDICAL_HISTORY":
      const index = state.medicalHistory.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedMedicalHistory = [...state.medicalHistory];
      if (index > -1) {
        updatedMedicalHistory[index] = action.payload;
      } else {
        updatedMedicalHistory.push(action.payload);
      }
      return { ...state, medicalHistory: updatedMedicalHistory };

    case "DELETE_MEDICAL_HISTORY":
      return {
        ...state,
        medicalHistory: state.medicalHistory.filter(
          (l) => l.id !== action.payload
        ),
      };
    case "SET_MEDICAL_HISTORY_FILES_TO_UPLOAD":
      return { ...state, medicalHistoriesFilestoUpload: action.payload };
    case "SET_MEDICAL_HISTORY_SAVING_NEW":
      return { ...state, medicalHistorySavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface MedicalHistoryStoreState {
  medicalHistory: MedicalHistoryObj[];
  medicalHistoryLoading: boolean;
  medicalHistorySavingNew: boolean;
  medicalHistoriesFilestoUpload: File[];
}

// Hook
export const useMedicalHistoryStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(medicalHistoryStoreReducer, {
    medicalHistory: [],
    medicalHistoryLoading: true,
    medicalHistoriesFilestoUpload: [],
    medicalHistorySavingNew: false,
  });

  const deleteMedicalHistoryById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchMedicalHistoryData = useV3GenericGetData<MedicalHistoryObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createMedicalHistory = useV3Create<MedicalHistoryObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateMedicalHistory = useV3Update<MedicalHistoryObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<MedicalHistoryObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleMedicalHistoryFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.medicalHistoriesFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "medical-histories",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_MEDICAL_HISTORY",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_MEDICAL_HISTORY_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.medicalHistoriesFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadMedicalHistoryDocument = async (
    medicalHistory_id: string,
    document_id: string | null
  ) => {
    if (!document_id || !medicalHistory_id || !ud_id) {
      return Promise.reject("Missing required parameters");
    }
    toast.success("Downloading file...");

    const params: any = {
      document_id: document_id,
      entity: "medical-histories",
      entity_id: medicalHistory_id,
      ud_id: ud_id,
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in medicalHistory objects' documents array
          downloadFileFromS3(
            res,
            state.medicalHistory
              ?.find((p) => p.id === medicalHistory_id)
              ?.documents?.find((d) => d.document_id === document_id)
              ?.document_name || "Snug_Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document with message "No response"`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
      });
  };

  const handleDeleteMedicalHistoryDocument = useCallback(
    async (
      medicalHistory_id: string,
      document_id: string | null
    ): Promise<void> => {
      if (!document_id || !medicalHistory_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "medical-histories",
          entity_id: medicalHistory_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the medicalHistory object
          const updatedMedicalHistory = state.medicalHistory.map((p) => {
            if (p.id === medicalHistory_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_MEDICAL_HISTORY",
            payload: updatedMedicalHistory,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.medicalHistory, deleteDocumentById, ud_id]
  );

  const handleMedicalHistoryFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_MEDICAL_HISTORY_FILES_TO_UPLOAD",
        payload: [...state.medicalHistoriesFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.medicalHistoriesFilestoUpload]
  );

  const handleMedicalHistoryFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.medicalHistoriesFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_MEDICAL_HISTORY_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.medicalHistoriesFilestoUpload]
  );

  const handleMedicalHistoryFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_MEDICAL_HISTORY_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchMedicalHistoryItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchMedicalHistoryData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "medical-histories",
        });

        if (isMounted()) {
          dispatch({ type: "SET_MEDICAL_HISTORY", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchMedicalHistoryData, isMounted]
  );

  const handleAddOrUpdateMedicalHistorySingleCall = useCallback(
    async (
      medicalHistoryItem: MedicalHistoryObj
    ): Promise<MedicalHistoryObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(medicalHistoryItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateMedicalHistory(
            {
              ud_id: ud_id,
              entity: "medical-histories",
              entity_id: medicalHistoryItem.id || undefined,
            },
            medicalHistoryItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createMedicalHistory(
            { ud_id: ud_id, entity: "medical-histories" }, // Adjust according to your actual API parameter requirements
            medicalHistoryItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_MEDICAL_HISTORY", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save Medical History item.");
        console.error(error);
        return null;
      }
    },
    [ud_id, createMedicalHistory, updateMedicalHistory]
  );

  const handleAddOrUpdateMedicalHistory = useCallback(
    async (medicalHistoryItem: MedicalHistoryObj) => {
      dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: true });
      if (state.medicalHistoriesFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateMedicalHistorySingleCall(
            medicalHistoryItem
          );
          if (res) {
            await handleMedicalHistoryFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical History data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateMedicalHistorySingleCall(medicalHistoryItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical History data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateMedicalHistorySingleCall,
      state.medicalHistoriesFilestoUpload,
      handleMedicalHistoryFilesUpload,
    ]
  );

  const handleDeleteMedicalHistory = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteMedicalHistoryById({
          ud_id: ud_id,
          entity: "medical-histories",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_MEDICAL_HISTORY", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_MEDICAL_HISTORY_SAVING_NEW", payload: false });
      }
    },
    [deleteMedicalHistoryById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchMedicalHistoryItems(ud_id); // Fetch all medicalHistory items
    }
  }, [ fetchMedicalHistoryItems, ud_id]);

  return {
    medicalHistory: state.medicalHistory,
    medicalHistoryLoading: state.medicalHistoryLoading,
    medicalHistorySavingNew: state.medicalHistorySavingNew,
    medicalHistoriesFilestoUpload: state.medicalHistoriesFilestoUpload,
    handleAddOrUpdateMedicalHistory,
    handleDeleteMedicalHistory,
    handleMedicalHistoryFileRemove,
    handleMedicalHistoryFilesDrop,
    handleMedicalHistoryFilesRemoveAll,
    handleDownloadMedicalHistoryDocument,
    handleDeleteMedicalHistoryDocument,
    handleMedicalHistoryFilesUpload,
  };
};
